import React, { useState, useEffect } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Sparkles, Wand2, Lightbulb, WandSparkles } from "lucide-react";
import { FormInfo, FormServices, UpsertFormRequest } from "@/services/bedrock";
import { useMutation } from "@tanstack/react-query";

type Step = "CHOICE" | "GENERATE_PROMPT";

interface FormCreateAIDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (draft: UpsertFormRequest) => void;
}

interface ExampleType {
  label: string;
  prompt: string;
}

const EXAMPLES: ExampleType[] = [
  {
    label: "A post-closing satisfaction survey with review links",
    prompt: "A post-closing satisfaction survey with review links",
  },
  {
    label: "A lead generation form for new mortgage inquiries",
    prompt: "A lead generation form for new mortgage inquiries",
  },
  {
    label: "Private feedback after a bad review",
    prompt:
      "A form with a single text field to collect feedback from customers who gave a bad review. Make sure it contains only a single text field, with no rating/stars or content/radio/other fields.",
  },
  {
    label: "A quick 3-question survey about social media content preferences",
    prompt: "A quick 3-question survey about social media content preferences",
  },
];

const LOADING_MESSAGES = [
  "Drawing up a winning playbook for your custom form...",
  "Coaching our AI on your specific goals...",
  "Designing high-performance fields optimized for conversions...",
  "Prepping the layout for maximum response rates...",
  "Polishing the form flow so your brand can shine...",
  "Applying behavioral coaching science to boost conversion plays...",
  "Crafting engaging questions and layout blocks...",
  "Assembling your custom, conversion-optimized form...",
  "Refining the fields to make your brand stand out...",
  "Huddling up! Finalizing your form's structure...",
];

interface AILoadingStateProps {
  messages?: string[];
  intervalMs?: number;
}

function AILoadingState({ messages = LOADING_MESSAGES, intervalMs = 4000 }: AILoadingStateProps) {
  const [currentMessage, setCurrentMessage] = useState(() => {
    const randomIndex = Math.floor(Math.random() * messages.length);
    return messages[randomIndex];
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessage(prev => {
        const remaining = messages.filter(m => m !== prev);
        const randomIndex = Math.floor(Math.random() * remaining.length);
        return remaining[randomIndex];
      });
    }, intervalMs);

    return () => clearInterval(interval);
  }, [messages, intervalMs]);

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center animate-in fade-in duration-500">
      <div className="relative mb-10">
        <div className="absolute inset-0 scale-150 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="relative z-10 flex items-center justify-center size-24 bg-white rounded-3xl shadow-xl border border-primary/10">
          <WandSparkles className="size-10 text-primary animate-pulse" />
        </div>
      </div>

      <div className="flex flex-col items-center gap-2 mb-8">
        <img alt={"SocialCoach AI"} src="https://cdn.socialcoach.io/socoai_color_positive.svg" className="h-[25px]" />
        <div className="text-xl font-bold text-slate-800 tracking-tight">Building your form...</div>
        <div className="text-muted-foreground text-sm max-w-sm mx-auto font-sans italic transition-all duration-500">
          "{currentMessage}"
        </div>
      </div>

      <div className="w-48 bg-muted rounded-full h-1.5 overflow-hidden">
        <div className="bg-primary h-1.5 rounded-full animate-pulse w-full" />
      </div>
    </div>
  );
}

export function FormCreateAIDialog({ open, onOpenChange, onSuccess }: FormCreateAIDialogProps) {
  const [step, setStep] = useState<Step>("CHOICE");
  const [prompt, setPrompt] = useState("");

  useEffect(() => {
    if (open) {
      setStep("CHOICE");
      setPrompt("");
    }
  }, [open]);

  const generateMutation = useMutation({
    mutationFn: (p: string) => FormServices.generateForm(p),
    onSuccess: (data: FormInfo) => {
      onSuccess({
        active: true,
        allowThemeChange: true,
        allowAiPersonalization: true,
        allowTranslations: false,
        supportedLanguages: ["en"],
        outroCtaLabel: "Share with a friend",
        outroCtaType: "share_player_link",
        outroCtaLink: undefined,
        displayName: data.displayName,
        introTitle: data.introTitle || "",
        introText: data.introText || "",
        outroTitle: data.outroTitle || "",
        outroText: data.outroText || "",
        formFields: data.formFields.map(f => ({
          fieldKey: f.fieldKey,
          active: true,
          title: f.title,
          subtitle: f.subtitle || "",
          fieldType: f.fieldType,
          required: f.required,
          position: f.position,
          fieldOptions: f.fieldOptions || {},
        })),
      });
    },
  });

  const handleGenerate = (rawPrompt: string) => {
    let finalPrompt = rawPrompt;
    const matchedExample = EXAMPLES.find(e => e.label === rawPrompt);
    if (matchedExample) {
      finalPrompt = matchedExample.prompt;
    } else if (
      rawPrompt.toLowerCase().includes("bad review") &&
      !rawPrompt.toLowerCase().includes("only a text field")
    ) {
      finalPrompt = `${rawPrompt} (Note: Create only a single text field, with no rating/stars or content/radio/other fields.)`;
    }
    generateMutation.mutate(finalPrompt);
  };

  const handleCreateManually = () => {
    onSuccess({
      displayName: "New Feedback Form",
      active: true,
      allowThemeChange: true,
      allowAiPersonalization: true,
      allowTranslations: false,
      supportedLanguages: ["en"],
      outroCtaLabel: "Share with a friend",
      outroCtaType: "share_player_link",
      outroCtaLink: undefined,
      formFields: [],
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (generateMutation.isPending) {
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      size="xl"
      showCloseButton={!generateMutation.isPending}
      disableEscapeDismissal={generateMutation.isPending}
    >
      <Dialog.Header>
        <Dialog.Title>
          {generateMutation.isPending && "Generating Form"}
          {!generateMutation.isPending && step === "CHOICE" && "Create New Form"}
          {!generateMutation.isPending && step === "GENERATE_PROMPT" && (
            <div className="text-slate-950 flex flex-row gap-1.5 items-center">
              Generate a new form with{" "}
              <img
                alt={"SocialCoach AI"}
                src="https://cdn.socialcoach.io/socoai_mono_positive.svg"
                className="h-[14px]"
              />
            </div>
          )}
        </Dialog.Title>
        <Dialog.Description>
          {generateMutation.isPending && "Please wait while our AI drafts your custom form fields..."}
          {!generateMutation.isPending && step === "CHOICE" && "How would you like to start?"}
          {!generateMutation.isPending &&
            step === "GENERATE_PROMPT" &&
            "Tell us what kind of form you need, and our AI will build it for you."}
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="max-h-[65vh] overflow-y-auto">
        {generateMutation.isPending && <AILoadingState />}

        {!generateMutation.isPending && step === "CHOICE" && (
          <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-2">
            <button
              onClick={() => setStep("GENERATE_PROMPT")}
              className="flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed border-border p-8 transition-colors hover:border-primary hover:bg-primary/5 group"
            >
              <div className="rounded-full bg-primary/10 p-4 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                <Sparkles className="h-8 w-8" />
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-slate-950 font-sans flex flex-row gap-1.5 items-center justify-center">
                  Generate with{" "}
                  <img
                    alt={"SocialCoach AI"}
                    src="https://cdn.socialcoach.io/socoai_mono_positive.svg"
                    className="h-[15px]"
                  />
                </div>
                <div className="text-sm text-muted-foreground font-sans">
                  Build a complete form from a simple description.
                </div>
              </div>
            </button>
            <button
              onClick={handleCreateManually}
              className="flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed border-border p-8 transition-colors hover:border-primary hover:bg-primary/5 group"
            >
              <div className="rounded-full bg-secondary/10 p-4 text-secondary group-hover:bg-secondary group-hover:text-white transition-colors">
                <Wand2 className="h-8 w-8" />
              </div>
              <div className="text-center">
                <div className="font-bold text-lg text-slate-950 font-sans">Create Manually</div>
                <div className="text-sm text-muted-foreground font-sans">
                  Start from scratch and add fields one by one.
                </div>
              </div>
            </button>
          </div>
        )}

        {!generateMutation.isPending && step === "GENERATE_PROMPT" && (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_300px] gap-4 py-2">
            {/* Left Column: Instructions Card */}
            <div className="flex flex-col gap-4 h-[350px]">
              <Card className="p-4 bg-slate-50 border-none shadow-none flex flex-col h-full w-full">
                <div className="flex flex-col gap-3 h-full">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Describe your form</label>
                  <Textarea
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                    placeholder="e.g. A survey for new home buyers to rate their experience..."
                    className="flex-1 bg-white border-slate-200 resize-none rounded-xl p-3 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm font-sans"
                  />
                </div>
              </Card>
            </div>

            {/* Right Column: Examples / Try an Idea */}
            <div className="flex flex-col gap-4 bg-slate-50 rounded-2xl p-4 h-[350px]">
              <div className="flex items-center gap-2 text-slate-900 font-bold text-sm shrink-0">
                <Lightbulb className="size-4 text-amber-500" />
                Try an idea
              </div>

              <div className="flex flex-col gap-2 overflow-y-auto pr-1 custom-scrollbar">
                {EXAMPLES.map((example, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setPrompt(example.label);
                      handleGenerate(example.label);
                    }}
                    className="text-left cursor-pointer font-sans text-sm text-slate-600 hover:text-primary hover:bg-white p-2.5 rounded-xl border border-transparent hover:border-primary/20 transition-all duration-200 bg-slate-100/50 leading-snug"
                  >
                    {example.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </Dialog.Content>

      <Dialog.Footer>
        {generateMutation.isPending ? (
          <div className="flex justify-center w-full py-2">
            <span className="text-xs text-muted-foreground italic flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              This can take up to a minute. Please don't close this window.
            </span>
          </div>
        ) : (
          <div className="flex justify-between w-full items-center">
            <div>
              {step !== "CHOICE" && (
                <Button variant="ghost" onClick={() => setStep("CHOICE")}>
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>

              {step === "GENERATE_PROMPT" && (
                <Button onClick={() => handleGenerate(prompt)} loading={generateMutation.isPending} disabled={!prompt}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Form
                </Button>
              )}
            </div>
          </div>
        )}
      </Dialog.Footer>
    </Dialog>
  );
}
